/**
 * Shared outbound WebSocket setup for CLI bridge, firehose, and pending auth:
 * TLS agent and consistent close / error logging.
 */

import https from 'node:https';
import WebSocket from 'ws';
import type { LogFn } from '../log.js';

/** Outbound WebSocket ping interval (bridge + firehose) so proxies/NAT do not drop idle sockets. */
export const CLI_WEBSOCKET_CLIENT_PING_MS = 25_000;

/**
 * Start sending WebSocket `ping` frames on an interval. Call from the socket's `open` handler.
 * Returns a disposer — call on `close` / `error` / before replacing the socket.
 */
export function attachWebSocketClientPing(ws: WebSocket, intervalMs: number): () => void {
  let timer: ReturnType<typeof setInterval> | null = null;
  function clear(): void {
    if (timer != null) {
      clearInterval(timer);
      timer = null;
    }
  }
  clear();
  timer = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      try {
        ws.ping();
      } catch {
        /* ignore */
      }
    }
  }, intervalMs);
  return clear;
}

export function buildCliWebSocketClientOptions(wsUrl: string): WebSocket.ClientOptions {
  const wsOptions: WebSocket.ClientOptions = { perMessageDeflate: false };
  if (wsUrl.startsWith('wss://')) {
    wsOptions.agent = new https.Agent({ rejectUnauthorized: false });
  }
  return wsOptions;
}

/** Log a WebSocket `error` event; optional `detail` is inserted after "WebSocket error" (e.g. "while waiting for sign-in"). */
export function logCliWebSocketError(
  log: LogFn,
  serviceLabel: string,
  err: Error,
  detail?: string,
): void {
  const mid = detail ? ` ${detail}` : '';
  log(`${serviceLabel} WebSocket error${mid}: ${err.message}`);
}

/**
 * Close the socket and drop listeners once closed. Avoids stripping listeners on a still-connecting
 * socket before `close` finishes (Node can surface that as an unhandled `error`).
 */
export function safeCloseWebSocket(ws: WebSocket): void {
  try {
    if (ws.readyState === WebSocket.CLOSED) {
      ws.removeAllListeners();
      return;
    }
    ws.once('close', () => {
      ws.removeAllListeners();
    });
    ws.close();
  } catch {
    try {
      ws.removeAllListeners();
    } catch {
      /* ignore */
    }
  }
}

/** Send a binary frame if the socket is open; swallow send failures (half-open / closing). */
export function safeSendWebSocketBinary(ws: WebSocket, data: Buffer): boolean {
  if (ws.readyState !== WebSocket.OPEN) return false;
  try {
    ws.send(data, { binary: true });
    return true;
  } catch {
    return false;
  }
}
